/* @flow */
import { combineReducers } from 'redux-immutable';
import { Record, type Map, OrderedMap, Set } from 'immutable';
import type { Action } from 'trustar/utils/redux-types.js';
import { pipe, map } from 'ramda';
import { listToOrderedMap } from 'trustar/utils';
import jstz from 'jstz';
import { pickImmutable } from 'trustar/utils';

import {
  SET_ENCLAVE_MENU,
  TIMEZONE_CHANGED,
  SAVE_EMAIL_SETTINGS,
  SAVE_EMAIL_SETTINGS_SUCCESS,
  SAVE_EMAIL_SETTINGS_FAILURE,
  FETCH_INGEST_EMAILS,
  FETCH_INGEST_EMAILS_SUCCESS,
  FETCH_INGEST_EMAILS_FAILURE,
  DELETE_INGEST_EMAIL,
  DELETE_INGEST_EMAIL_SUCCESS,
  DELETE_INGEST_EMAIL_FAILURE,
  EDIT_EMAIL_INGEST,
  CLEAR_EMAIL_INGEST,
  TOGGLE_SELECT_EMAIL_INGEST,
  CLEAR_SELECTED_EMAIL_INGEST,
  CREATE_NEW_EMAIL_INGEST,
  CLOSE_NEW_EMAIL_FORM,
  CONFIRM_DELETE_INGEST_EMAIL,
  CANCEL_CONFIRM_DELETE_INGEST_EMAIL,
  CONFIRM_REQUEST_NEW_INGEST_EMAIL,
  CANCEL_CONFIRM_REQUEST_NEW_INGEST_EMAIL,
} from './action-types.js';

const getCurrentTimezone = () => {
  return jstz.determine().name();
};

const Settings = Record({
  enclaveMenuOpen: false,
  timezone: getCurrentTimezone(), // Default to browser-determined timezone
});

const enclaveMenuOpen = (state = false, action) => {
  const { type, payload } = action;

  switch (type) {
  case SET_ENCLAVE_MENU:
    return payload;
  default:
    return state;
  }
};

const timezone = (state = getCurrentTimezone(), action) => {
  const { type, payload } = action;

  switch (type) {
  case TIMEZONE_CHANGED:
    return payload;
  default:
    return state;
  }
};

const ingestEmails = (state = OrderedMap(), action) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_INGEST_EMAILS_SUCCESS: {
    const isFirstPage = action.meta && action.meta.startPage === 0;
    return isFirstPage ? payload : state.merge(payload); // Append (merge) if we're paginating
  }

  case DELETE_INGEST_EMAIL_SUCCESS:
    return state.delete(payload);

  default:
    return state;
  }
};

const Options = Record({
  hasNext: false,
  nextPage: 0,
  startPage: 0,
  pageSize: 25,
  sortBy: 'ingestEmail', // See NOTE
  sortOrder: 'ASC',
});

const emailOptions = (state = Options(), action) => {
  const { type, meta } = action;

  switch (type) {
  case FETCH_INGEST_EMAILS_SUCCESS:
    return state
      .set('nextPage', meta.startPage + 1)
      .set('hasNext', meta.hasNext);
  default:
    return state;
  }
};

const emailLoading = (state = false, action) => {
  const { type } = action;

  switch (type) {
  case FETCH_INGEST_EMAILS:
  case SAVE_EMAIL_SETTINGS:
  case DELETE_INGEST_EMAIL:
    return true;
  case FETCH_INGEST_EMAILS_SUCCESS:
  case FETCH_INGEST_EMAILS_FAILURE:
  case DELETE_INGEST_EMAIL_SUCCESS:
  case DELETE_INGEST_EMAIL_FAILURE:
  case SAVE_EMAIL_SETTINGS_SUCCESS:
  case SAVE_EMAIL_SETTINGS_FAILURE:
    return false;
  default:
    return state;
  }
};

const creatingAdditionalInbox = (state = false, action) => {
  const { type } = action;

  switch (type) {
  case CREATE_NEW_EMAIL_INGEST:
    return true;
  case SAVE_EMAIL_SETTINGS_SUCCESS:
  case CLOSE_NEW_EMAIL_FORM:
    return false;
  default:
    return state;
  }
};

// Represents the current EI being edited
const currentIngestEmailId = (state = null, action) => {
  const { type, payload } = action;

  switch (type) {
  case EDIT_EMAIL_INGEST:
    return payload.guid;
  case SAVE_EMAIL_SETTINGS_SUCCESS: // Reset on successful save
  case CLEAR_EMAIL_INGEST:
    return null;
  default:
    return state;
  }
};

const totalEmailCount = (state = 0, action) => {
  const { type, meta } = action;

  switch (type) {
  case FETCH_INGEST_EMAILS_SUCCESS:
    return meta.total;
  case DELETE_INGEST_EMAIL_SUCCESS:
    return state - 1;
  default:
    return state;
  }
};

const selectedIngestEmails = (state = null, action) => {
  const {type, payload} = action;
  switch(type) {
  case TOGGLE_SELECT_EMAIL_INGEST:
    if (state === payload.guid) return null;
    return payload.guid;
  case DELETE_INGEST_EMAIL_SUCCESS:
  case SAVE_EMAIL_SETTINGS_SUCCESS:
  case CLEAR_SELECTED_EMAIL_INGEST:
    return null;
  default:
    return state;
  }
};

const showDeleteIngestModal = (state = false, action) => {
  const {type, payload} = action;

  switch (type) {
  case CONFIRM_DELETE_INGEST_EMAIL:
    return true;
  case DELETE_INGEST_EMAIL:
  case DELETE_INGEST_EMAIL_SUCCESS:
  case DELETE_INGEST_EMAIL_FAILURE:
  case CANCEL_CONFIRM_DELETE_INGEST_EMAIL:
    return false;
  default:
    return state;
  }
};

const showRequestNewIngestModal = (state = false, action) => {
  const {type, payload} = action;

  switch (type) {
  case CONFIRM_REQUEST_NEW_INGEST_EMAIL:
    return true;
  case CANCEL_CONFIRM_REQUEST_NEW_INGEST_EMAIL:
    return false;
  default:
    return state;
  }
};

export const serialize = (state: Map<any, any>) =>
  pickImmutable(['timezone'], state);

export default combineReducers({
  ingestEmails,
  emailOptions,
  emailLoading,
  timezone,
  enclaveMenuOpen,
  currentIngestEmailId,
  totalEmailCount,
  selectedIngestEmails,
  creatingAdditionalInbox,
  showDeleteIngestModal,
  showRequestNewIngestModal
});



// WEBPACK FOOTER //
// ./src/app/modules/settings/stores.js